import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./Modal.module.css";
import { CertFile } from "../../../../../utils/nonCredlyCerts";

/**
 * ============================================================================
 * THE SHARED CERTIFICATE LIGHTBOX — ten credentials, one render path.
 * ============================================================================
 * Every non-Credly certificate on the site opens through this component, which
 * is why the geometry defect it carried was ten defects: at 1280 in both themes
 * each scan was clipped by 36–113.6px off the top AND the bottom, so the
 * signatory block at the foot of the certificate was simply not on screen. The
 * cause and the fix are in `Modal.module.css` beside the rules that carry them.
 *
 * ----------------------------------------------------------------------------
 * WHAT THE KEYBOARD CONTRACT WAS MISSING
 * ----------------------------------------------------------------------------
 * `CertificateShowcase` has said so in its own docblock since D-076: this modal
 * had Escape, `role="dialog"`, `aria-modal` and focus moved in on open, and was
 * missing the two things a keyboard user actually needs.
 *
 *   · **Focus returns to the trigger on close.** Without it the next Tab
 *     restarts at the top of the document with nothing on screen having
 *     changed — the D-068 finding about the mobile menu, in another control.
 *     The opener is captured from `document.activeElement` rather than passed
 *     in, because ten different chips open this one dialog and none of them
 *     should have to know that.
 *   · **Tab is trapped inside the dialog**, or the user tabs into a page they
 *     cannot see behind a backdrop they cannot dismiss.
 *
 * `role="dialog"` and `aria-modal` also moved OFF the backdrop and onto the
 * panel. On the backdrop they named the scrim as the dialog, so the accessible
 * dialog included the full-viewport click-to-close layer; the panel is the
 * thing with the content in it. The backdrop is `role="presentation"`, which is
 * what `CertificateShowcase` does.
 */

interface Props {
	open: boolean;
	file: CertFile | null;
	onClose: () => void;
}

const modalVariants = {
	hidden: { opacity: 0, scale: 0.96 },
	visible: {
		opacity: 1,
		scale: 1,
		transition: { duration: 0.25, ease: "easeOut" }
	},
	exit: { opacity: 0, scale: 0.96 }
};

export default function CertificateModal({ open, file, onClose }: Props) {
	const dialogRef = useRef<HTMLDivElement>(null);
	/** Whatever had focus when this opened — a chip, in every current path. */
	const openerRef = useRef<HTMLElement | null>(null);

	/* ================= ESC + FOCUS TRAP + FOCUS RETURN ================= */
	useEffect(() => {
		if (!open) return undefined;

		openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key !== "Tab") return;

			/* Queried live rather than captured on open, so a re-render inside
			   the dialog cannot leave the trap pointing at a detached node. */
			const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
				"a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])"
			);
			if (!focusable || focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKeyDown);
		dialogRef.current?.focus();

		return () => {
			document.removeEventListener("keydown", onKeyDown);
			openerRef.current?.focus();
		};
	}, [open, onClose]);

	return (
		<AnimatePresence>
			{open && file && (
				<motion.div
					className={styles.backdrop}
					onClick={onClose}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					role="presentation"
				>
					<motion.div
						ref={dialogRef}
						className={styles.modal}
						role="dialog"
						aria-modal="true"
						aria-label={file.label}
						tabIndex={-1}
						variants={modalVariants}
						initial="hidden"
						animate="visible"
						exit="exit"
						onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
					>
						<header className={styles.header}>
							<div>
								<h4>{file.label}</h4>
								<span>
									{file.issuedDate && `Issued ${file.issuedDate}`}
									{file.credentialId && ` · ID: ${file.credentialId}`}
								</span>
							</div>
							<button className={styles.close} onClick={onClose} aria-label="Close certificate preview">
								✕
							</button>
						</header>

						<div className={styles.body}>
							<img src={file.src} alt={file.label} loading="lazy" draggable={false} />
						</div>

						<footer className={styles.footer}>
							<a href={file.src} download>
								Download Certificate
							</a>
						</footer>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
